<template>
  <ModalDialog
    header-text="Schedule A Options"
    contentClass="downloads-modal-content"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <IngredientPlantsGrid :plants="plantsWithSelection" />
      <div class="downloads-sort-options" data-cy="downloads-sort-options">
        <p data-cy="sort-text">Sort (for PDF only):</p>
        <RadioButton
          v-for="option in sortOptions"
          name="sort-group"
          :value="option.value"
          :key="option.name"
          :label="option.name"
          :checked="option.value === selectedSortOption.value"
          @change="selectedSortOption = option"
          data-cy="sort-radio-button"
        />
      </div>
      <Checkbox
        class="view-all-companies-checkbox"
        v-if="viewAllCompaniesAndHistoryVisible"
        v-model="viewAllCompaniesChecked"
        label="Select ONE plant only. View Schedule A for all Companies in Plant"
        data-cy="view-all-companies-checkbox"
      />
      <div v-if="viewAllCompaniesAndHistoryVisible" class="schedule-history">
        <div class="date-picker-control">
          <span data-cy="history-date-label">Begin Date:</span>
          <DatePicker
            class="history-date-picker"
            :default-value="historyBeginDate"
            :max="new Date()"
            :popup-settings="{ popupClass: 'modal-popup' }"
            @change="historyBeginDate = $event.value || new Date()"
            data-cy="history-date-picker"
          />
        </div>
        <Checkbox
          v-model="historyIncludeUnmodifiedRecords"
          label="Include Unmodified Records"
          data-cy="include-unmodified-records-checkbox"
        />
      </div>
      <div class="downloads-actions">
        <div class="downloads-actions-left">
          <LoadingButton
            v-if="viewAllCompaniesAndHistoryVisible"
            type="button"
            theme-color="success"
            :loading="loading"
            :disabled="downloadDisabled"
            @click="exportHistoryClickedAsync"
            data-cy="history-download-button"
            >Export Schedule A History</LoadingButton
          >
        </div>
        <div class="downloads-actions-right">
          <LoadingButton
            type="button"
            theme-color="primary"
            :loading="loading"
            :disabled="downloadDisabled"
            @click="pdfDownloadClickedAsync"
            data-cy="pdf-download-button"
            >Download PDF</LoadingButton
          >
          <LoadingButton
            type="button"
            theme-color="primary"
            :loading="loading"
            :disabled="downloadDisabled"
            @click="excelDownloadClickedAsync"
            data-cy="excel-download-button"
            >Download Excel</LoadingButton
          >
        </div>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import type { IdAndName } from "@/Domain/IdAndName";
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import IngredientPlantsGrid from "./IngredientPlantsGrid.vue";
import type { PlantWithSelection } from "@/api/scheduleB";
import { computed, ref } from "vue";
import { Checkbox } from "@progress/kendo-vue-inputs";
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { ScheduleHistoryGroup } from "@/shared/access/RoleGroups";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import moment from "moment";
import RadioButton from "@/shared/ui/inputs/RadioButton.vue";

export interface SortOption {
  name: string;
  value: string;
}

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  plants: IdAndName[];
}>();

const emit = defineEmits(["closeModal"]);

const sortOptions = [
  {
    name: "Ingredient Name",
    value: "LabelName",
  },
  {
    name: "Source Company Name",
    value: "DistributorCompanyName",
  },
  {
    name: "Raw Material Code",
    value: "Rmc",
  },
  {
    name: "Group",
    value: "Group",
  },
] as SortOption[];

const selectedSortOption = ref<SortOption>(sortOptions[0]);
const viewAllCompaniesChecked = ref(false);
const historyBeginDate = ref<Date>(moment().subtract(1, "months").toDate());
const historyIncludeUnmodifiedRecords = ref(false);
const loading = ref(false);

const plantsWithSelection = computed(() => {
  return props.plants as PlantWithSelection[];
});

const anyPlantSelected = computed(() => {
  return plantsWithSelection.value.some((p) => p.selected);
});

const moreThanOnePlantSelected = computed(() => {
  return plantsWithSelection.value.filter((p) => p.selected).length > 1;
});

const viewAllCompaniesAndHistoryVisible = computed(() => {
  return ScheduleHistoryGroup.roles.some((r) => main.isInRole(r));
});

const downloadDisabled = computed(() => {
  return (
    !anyPlantSelected.value ||
    (viewAllCompaniesChecked.value && moreThanOnePlantSelected.value)
  );
});

const pdfDownloadClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ScheduleADownloadPdf", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    sort: selectedSortOption.value.value,
    allCompaniesInPlant: viewAllCompaniesChecked.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "pdfDownloadClickedAsync",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading PDF file. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const excelDownloadClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ScheduleADownloadExcel", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    allCompaniesInPlant: viewAllCompaniesChecked.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "excelDownloadClickedAsync",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error downloading Excel file. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const exportHistoryClickedAsync = async function () {
  if (loading.value) return;
  loading.value = true;

  const downloadPromise = api.getDownload("ExportScheduleAHistory", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    plantList: plantsWithSelection.value
      .filter((p) => p.selected)
      .map((p) => p.ID)
      .join(";"),
    beginDate: historyBeginDate.value.toLocaleString("en-US"),
    includeUnmodified: historyIncludeUnmodifiedRecords.value,
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {},
    "DownloadsModal.vue",
    "exportHistoryClickedAsync",
    (error) => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error exporting Schedule A history. ${error}`,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const closeModal = function () {
  emit("closeModal");
};
</script>

<style lang="scss">
.downloads-modal-content {
  max-width: 600px;

  .plant-grid {
    max-height: 360px;
    margin-bottom: 20px;
  }

  .downloads-sort-options {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    padding-left: 8px;

    p {
      margin-bottom: 10px;
    }
  }

  .view-all-companies-checkbox {
    padding-left: 8px;
  }

  .schedule-history {
    display: flex;
    flex-direction: column;
    margin: 20px 0;
    padding-left: 8px;

    .date-picker-control {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 12px;

      .history-date-picker {
        width: auto;

        .k-input-inner {
          border: 0;
        }
      }
    }
  }

  .downloads-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 8px;

    &-left {
      flex: 1;
    }

    &-right {
      flex: 1;
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }
  }
}
</style>
